import { useEffect, useState } from "react";
import { api } from "../lib/api";
import { TiktokLogo, CheckCircle, WarningCircle, ArrowSquareOut, Plug } from "@phosphor-icons/react";
import { toast } from "sonner";
import PublishingPauseBanner from "../components/PublishingPauseBanner";
import { PUBLIC_HOST, PUBLIC_ORIGIN } from "../lib/domain";

export default function TikTokConnectModule() {
  const [health, setHealth] = useState(null);
  const [loading, setLoading] = useState(true);

  async function load() {
    try {
      const r = await api.get("/tiktok/health");
      setHealth(r.data);
    } catch (e) {
      toast.error("Impossible de charger l'état TikTok");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { load(); }, []);

  async function connect(minimal = false) {
    try {
      const r = await api.get(`/tiktok/oauth/login${minimal ? "?minimal=true" : ""}`);
      if (r.data.auth_url) {
        window.location.href = r.data.auth_url;
      }
    } catch (e) {
      const msg = e.response?.data?.detail || e.message;
      toast.error(`Impossible de démarrer la connexion : ${msg}`);
    }
  }

  if (loading) return <div className="p-6 text-zinc-400">Chargement…</div>;

  const configured = health?.configured;
  const connected = health?.connected;
  const scopes = health?.scopes || [];
  const canPublish = scopes.includes("video.publish") || scopes.includes("video.upload");

  return (
    <div className="p-6 max-w-3xl mx-auto space-y-6" data-testid="tiktok-connect-module">
      <PublishingPauseBanner />
      <header className="border-b border-zinc-800 pb-4">
        <div className="flex items-center gap-2 text-pink-400">
          <TiktokLogo size={22} weight="fill" />
          <span className="text-[10px] uppercase tracking-[0.3em] font-bold">TikTok Business</span>
        </div>
        <h1 className="text-3xl font-bold text-zinc-100 mt-1">Connecter @institutimpact0</h1>
        <p className="text-sm text-zinc-500 mt-1">
          Publication automatique vidéo + caption depuis Kaizen vers ton compte TikTok officiel.
        </p>
      </header>

      <div className="grid gap-3">
        <Status
          ok={configured}
          label="Application TikTok configurée"
          detail={configured ? "Client Key, Secret et Redirect URI présents" : "Variables manquantes dans backend/.env"}
          testid="status-configured"
        />
        <Status
          ok={connected}
          label="Compte TikTok connecté"
          detail={
            connected
              ? `@${health.username || health.open_id?.slice(0, 12)} · scopes : ${(health.scopes || []).join(", ")}`
              : "Aucun compte autorisé pour le moment"
          }
          testid="status-connected"
        />
      </div>

      {!connected && configured && (
        <div className="space-y-2">
          <button
            onClick={() => connect(false)}
            data-testid="btn-connect-tiktok"
            className="w-full px-6 py-4 rounded-lg bg-pink-500 hover:bg-pink-400 text-white text-sm font-bold uppercase tracking-widest transition flex items-center justify-center gap-3 shadow-lg shadow-pink-500/20"
          >
            <TiktokLogo size={22} weight="fill" />
            Autoriser @institutimpact0 → Kaizen
            <ArrowSquareOut size={16} />
          </button>
          <button
            onClick={() => connect(true)}
            data-testid="btn-connect-tiktok-minimal"
            className="w-full px-4 py-2.5 rounded-lg bg-zinc-800 hover:bg-zinc-700 text-zinc-300 text-xs font-semibold transition flex items-center justify-center gap-2"
          >
            🧪 Test connexion minimale (scope user.info.basic — sans audit vidéo)
          </button>
        </div>
      )}

      {connected && (
        <div className="p-4 rounded-lg bg-emerald-950/40 border border-emerald-800 text-sm text-emerald-200">
          <div className="flex items-center gap-2 font-bold">
            <CheckCircle size={18} weight="fill" /> Connecté à TikTok
          </div>
          <p className="mt-1 text-xs text-emerald-300/80">
            Token expire le {health.expires_at ? new Date(health.expires_at).toLocaleString("fr-CA") : "N/A"}
            (refresh automatique géré par Kaizen).
          </p>
        </div>
      )}

      {connected && !canPublish && (
        <div className="p-4 rounded-lg bg-amber-950/40 border border-amber-700 space-y-3" data-testid="tiktok-upgrade-publish">
          <div className="flex items-center gap-2 font-bold text-amber-200">
            <WarningCircle size={18} weight="fill" /> Publication automatique non activée
          </div>
          <p className="text-xs text-amber-200/80">
            Tu es connecté en lecture seule (<code>user.info.basic</code>). Pour que Kaizen <strong>publie automatiquement</strong> tes vidéos sur TikTok, reconnecte-toi avec les permissions de publication (<code>video.upload</code> + <code>video.publish</code>).
            <br />Assure-toi d'abord que ces scopes sont activés dans ton app TikTok (Live ✓).
          </p>
          <button
            onClick={() => connect(false)}
            data-testid="btn-upgrade-tiktok-publish"
            className="w-full px-6 py-3 rounded-lg bg-pink-500 hover:bg-pink-400 text-white text-sm font-bold uppercase tracking-widest transition flex items-center justify-center gap-3"
          >
            <TiktokLogo size={20} weight="fill" /> Activer la publication automatique
            <ArrowSquareOut size={16} />
          </button>
        </div>
      )}

      {connected && canPublish && (
        <div className="p-3 rounded-lg bg-pink-950/30 border border-pink-800 text-xs text-pink-200 flex items-center gap-2" data-testid="tiktok-publish-ready">
          <CheckCircle size={16} weight="fill" /> Publication automatique activée — Kaizen peut publier vidéos + caption sur @institutimpact0.
        </div>
      )}

      <section className="space-y-3 p-4 rounded-lg bg-zinc-900/60 border border-zinc-800">
        <h2 className="text-xs font-bold uppercase tracking-widest text-zinc-300">📋 Avant d'autoriser</h2>
        <ol className="text-sm text-zinc-400 list-decimal list-inside space-y-2">
          <li>
            Va sur{" "}
            <a href="https://developers.tiktok.com/apps" target="_blank" rel="noopener noreferrer" className="text-pink-400 hover:underline">
              developers.tiktok.com/apps
            </a>{" "}
            → ton app <strong className="text-zinc-200">Kaizen</strong>
          </li>
          <li>
            Dans <strong>URL Properties</strong>, ajoute et vérifie ton domaine de PRODUCTION (stable) :{" "}
            <code className="text-xs bg-zinc-950 px-1.5 py-0.5 rounded text-amber-300">{PUBLIC_HOST}</code>
            <span className="text-xs text-zinc-500"> (vérification via enregistrement DNS TXT dans Cloudflare — le domaine preview change à chaque fork, évite-le)</span>
          </li>
          <li>
            Dans <strong>Settings → Redirect URI</strong>, ajoute :{" "}
            <code className="text-xs bg-zinc-950 px-1.5 py-0.5 rounded text-amber-300 break-all">
              {PUBLIC_ORIGIN}/api/tiktok/oauth/callback
            </code>
            <span className="text-xs text-zinc-500"> (puis mets <code>TIKTOK_REDIRECT_URI</code> = cette URL dans les Secrets de PRODUCTION)</span>
          </li>
          <li>
            Dans <strong>Scopes</strong>, active :{" "}
            <code className="text-xs bg-zinc-950 px-1.5 py-0.5 rounded text-amber-300">user.info.basic, video.upload, video.publish</code>
          </li>
          <li>
            Sauvegarde, puis reviens ici et clique <strong>"Autoriser"</strong>.
          </li>
        </ol>
      </section>
    </div>
  );
}

function Status({ ok, label, detail, testid }) {
  return (
    <div
      data-testid={testid}
      className={`p-3 rounded-lg border flex items-start gap-3 ${
        ok ? "bg-emerald-950/30 border-emerald-800/60" : "bg-zinc-900/60 border-zinc-800"
      }`}
    >
      {ok ? <CheckCircle size={20} weight="fill" className="text-emerald-400 shrink-0 mt-0.5" /> : <WarningCircle size={20} className="text-amber-400 shrink-0 mt-0.5" />}
      <div className="flex-1 min-w-0">
        <div className={`text-sm font-bold ${ok ? "text-emerald-200" : "text-zinc-200"}`}>{label}</div>
        <div className="text-xs text-zinc-500 mt-0.5">{detail}</div>
      </div>
    </div>
  );
}
